/**
 * First-touch attribution (docs/ai-management-org/DESIGN_v2.md §7.3).
 *
 * Where a visitor first came from — UTM tags, which ad network's click,
 * the referring site, the landing page — kept in this browser only after
 * analytics consent, and sent once when someone signs in
 * (record_first_touch, 20270548000000). The click identifier itself
 * (fbclid, ttclid, gclid) is never kept, only which network it names, and
 * the landing page loses its query string.
 */
export interface FirstTouch {
  touched_at: string;
  landing_path: string;
  referrer_host: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  click_network: 'meta' | 'tiktok' | 'google' | null;
  platform: 'web' | 'android';
}

export const FIRST_TOUCH_KEY = 'kanfy-first-touch';
const SENT_KEY = 'kanfy-first-touch-sent';

const clip = (v: string | null, max: number) => (v ? v.trim().slice(0, max) || null : null);

/** The first touch this page load amounts to. Pure. */
export function firstTouchFrom(
  page: { search: string; pathname: string; referrer: string; host: string },
  now: Date,
  platform: 'web' | 'android',
): FirstTouch {
  const q = new URLSearchParams(page.search);
  let referrerHost: string | null = null;
  try {
    const host = page.referrer ? new URL(page.referrer).hostname.toLowerCase() : '';
    referrerHost = host && host !== page.host.toLowerCase() ? host : null;
  } catch {
    referrerHost = null;
  }
  const clickNetwork = q.has('fbclid') ? 'meta' : q.has('ttclid') ? 'tiktok' : q.has('gclid') ? 'google' : null;
  return {
    touched_at: now.toISOString(),
    landing_path: page.pathname.slice(0, 300),
    referrer_host: referrerHost,
    utm_source: clip(q.get('utm_source'), 100),
    utm_medium: clip(q.get('utm_medium'), 100),
    utm_campaign: clip(q.get('utm_campaign'), 150),
    utm_content: clip(q.get('utm_content'), 150),
    utm_term: clip(q.get('utm_term'), 150),
    click_network: clickNetwork,
    platform,
  };
}

function read(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function write(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage full or blocked: attribution is best effort.
  }
}

/** Keep this visit as the first touch unless one is already kept. Call only with analytics consent. */
export function keepFirstTouch(storage: Storage, touch: FirstTouch): void {
  if (read(storage, FIRST_TOUCH_KEY) || read(storage, SENT_KEY)) return;
  write(storage, FIRST_TOUCH_KEY, JSON.stringify(touch));
}

/** The kept first touch, if it has not been sent for anyone yet. */
export function unsentFirstTouch(storage: Storage): FirstTouch | null {
  if (read(storage, SENT_KEY)) return null;
  const raw = read(storage, FIRST_TOUCH_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as FirstTouch;
  } catch {
    return null;
  }
}

/**
 * Sent once per browser: the first person to sign in here is the one the
 * visit brought. Someone else signing in later on a shared device is not
 * attributed to it.
 */
export function markFirstTouchSent(storage: Storage): void {
  write(storage, SENT_KEY, '1');
  try {
    storage.removeItem(FIRST_TOUCH_KEY);
  } catch {
    // ignore
  }
}
